
import Model from "App/model";
import Group from "App/model/group";
import BoundingBox from "App/utils/bounding_box";
import {vec3} from "gl-matrix";
import {RenderElement} from "App/model/elements/element";
import IScene from "js_interface/src/scene_interface";
import IDocument from "js_interface/src/structural_elements/document_interface";
import IRenderDocument from "App/render_document_interface";
import Fitgl from "App/lib/SVDataExport";

export default
class ShadowVolumesGenerator {
    private _sceneAPI: IScene;
    private static ShadowVolumeDescriptionUIDGenerator: number = 1;
    private _sceneDocument: IDocument;
    private _renderDocument: IRenderDocument;
    private fitSVGeneratorModule: any = null;
    private SVDataExportAPI: any = null;
    private createdElements: [Model, RenderElement][] = [];

    constructor(aRenderDocument: IRenderDocument) {
        this._renderDocument = aRenderDocument;
        this._sceneAPI = this._renderDocument.getSceneAPI();
        this._sceneDocument = this._sceneAPI.getDocument();
    }

    private _findMatchingPolygonOffset(aPolygonOffset: { factor: number, units: number }, aPolygonOffsets: { factor: number, units: number }[]): any {
        for (let i = 0; i < aPolygonOffsets.length; i++) {
            if (aPolygonOffset.factor === aPolygonOffsets[i].factor &&
                aPolygonOffset.units === aPolygonOffsets[i].units) {
                return aPolygonOffsets[i];
            }
        }

        return null;
    }

    private _generateShadowVolumeForGroupRecursive(aModel: Model, aGroup: Group) {
        let polygonOffsets = [];

        if (Object.keys(aGroup.getElements()).length > 0) {
            // collect the geometry objects which can cast shadow and get sum of vertices count for these geometry objects
            let elements = aGroup.getElements();
            for (let key in elements) {
                let renderElement = elements[key];
                let geometryObjects = renderElement.getGeometryObjects();

                for (let i = 0; i < geometryObjects.length; i++) {
                    let geometryObject = geometryObjects[i];
                    if (geometryObject.canCastShadow()) {
                        let renderOptions = geometryObject.getRenderOptions();
                        let polygonOffset = {
                            factor: renderOptions.polygonOffsetFactor,
                            units: renderOptions.polygonOffsetUnits
                        };

                        let polygonOffsetBin = this._findMatchingPolygonOffset(polygonOffset, polygonOffsets);
                        if (!polygonOffsetBin) {
                            polygonOffsetBin = polygonOffset;
                            polygonOffsets.push(polygonOffset);
                            polygonOffsetBin.floatCount = 0;
                            polygonOffsetBin.geometryObjectsInvolvedInGroup = [];
                        }

                        // let poffset = geometryObject.getPolygonOffset();
                        polygonOffsetBin.geometryObjectsInvolvedInGroup.push(geometryObject);
                        polygonOffsetBin.floatCount += geometryObject.getGeometry().nonIndexedPositionsDesc.data.length;
                    }
                }
            }

            for (let poIndex = 0; poIndex < polygonOffsets.length; poIndex++) {
                let polygonOffsetBin = polygonOffsets[poIndex];

                let floatCount = polygonOffsetBin.floatCount;
                let byteCount = floatCount * 4;
                let sourceMemory = this.fitSVGeneratorModule._malloc(byteCount);
                let data = new Float32Array(this.fitSVGeneratorModule.buffer, sourceMemory, floatCount);

                // do nto create empty sv shapes
                if (data.length === 0) {
                    continue;
                }

                let sourceMemoryWriteIndex = 0;
                for (let i = 0; i < polygonOffsetBin.geometryObjectsInvolvedInGroup.length; i++) {
                    let geometryObject = polygonOffsetBin.geometryObjectsInvolvedInGroup[i];
                    let nonIndexedVericesArray = geometryObject.getGeometry().nonIndexedPositionsDesc.data;

                    for (let j = 0; j < nonIndexedVericesArray.length; j++) {
                        data[sourceMemoryWriteIndex] = nonIndexedVericesArray[j];
                        sourceMemoryWriteIndex++;
                    }
                }

                let facesCount = (polygonOffsetBin.floatCount / 3) / 3;
                let descriptor = this.SVDataExportAPI.createSVDataWrap(sourceMemory, facesCount);

                // add ShadowVolume model element with sides geometry to the document and render graph
                let sidesElement = aModel.addElement(
                    aGroup.getID(),
                    "ShadowVolume",
                    this._generateElementDescriptionJSON(),
                    this._generateSideGeometryObjectDescriptionJSON(descriptor, polygonOffsetBin.factor, polygonOffsetBin.units),
                    true
                );

                this.createdElements.push([aModel, sidesElement]);

                // add ShadowVolume element with caps geometry to the document and render graph
                let capElement = aModel.addElement(
                    aGroup.getID(),
                    "ShadowVolume",
                    this._generateElementDescriptionJSON(),
                    this._generateCapGeometryObjectDescriptionJSON(descriptor, polygonOffsetBin.factor, polygonOffsetBin.units),
                    true
                );

                this.createdElements.push([aModel, capElement]);
            }
        }

        for (let c = 0; c < aGroup.data.children.length; c++) {
            this._generateShadowVolumeForGroupRecursive(aModel, aGroup.data.children[c]);
        }
    }

    public release() {
        let renderElementPair;
        for (let i = 0; i < this.createdElements.length; i++) {
            renderElementPair = this.createdElements[i];
            renderElementPair[0].removeElement(renderElementPair[1]);
        }

        this.createdElements = [];
    }

    public generate() {
        if (!this._sceneAPI.shadowsModule.getShadowVolumesEnabled()) {
            return false;
        }

        //@ts-ignore
        this.fitSVGeneratorModule = Fitgl();
        this.SVDataExportAPI = new this.fitSVGeneratorModule.SVDataExportWrapper();

        let models: any = this._renderDocument.getModelsList(false);

        for (let m = 0; m < models.length; m++) {
            let gt = models[m].getGroupTree();
            if (!gt) {
                continue;
            }

            this._generateShadowVolumeForGroupRecursive(models[m], gt.getRoot());
        }

        return true;
    }

    private _generateElementDescriptionJSON() {
        let UID = ShadowVolumesGenerator.ShadowVolumeDescriptionUIDGenerator++;
        return {
            "type": "ShadowVolume",
            "id": "ShadowVolume" + "_" + UID,
            "UID": UID,
            "volumes": ["generated"]
        };
    }

    private _generateSideGeometryObjectDescriptionJSON(aDescriptor: any, aPolygonOffsetFactor: number, aPolygonOffsetUnits: number) {
        // this goes to gl buffer
        let sides = aDescriptor.get_sides();
        let sidesSize = aDescriptor.get_sidesSize();
        let sideData = new Float32Array(this.fitSVGeneratorModule.buffer, sides, sidesSize);

        let bbox = new BoundingBox(vec3.fromValues(-1, -1, -1), vec3.fromValues(1, 1, 1));

        // these are data to draw triangles from the buffer
        let sideAttribCount = this.SVDataExportAPI.get_maxMultiplicity() + 2;
        let sideVertexCount = aDescriptor.get_sidesSize() / 4; // 4 == attribute size
        let sideElements = [];
        let sideStrides = [];
        let sideOffsets = [];
        for (let i = 0; i < sideAttribCount; ++i) {
            let sideAttribs = aDescriptor.get_sidesVertAttrib(i);
            sideElements.push(sideAttribs.get_elements());
            sideStrides.push(sideAttribs.get_stride());
            sideOffsets.push(sideAttribs.get_offset());
        }

        return [{
            description: {
                type: "SVSIDESHAPE",
                bboxMin: vec3.clone(bbox.getMin()),
                bboxMax: vec3.clone(bbox.getMax())
            },
            appearance: {
                renderOption: {
                    polygonOffsetFactor: aPolygonOffsetFactor, // the 2.0 is used here to get rid of flickering the shadowed geometry
                    polygonOffsetUnits: aPolygonOffsetUnits, // the 2.0 is used here to get rid of flickering the shadowed geometry
                    isSide: true,
                    pickable: false
                }
            },
            svBinaryGeometry: {
                src: this._sceneAPI.getDocument().createLocalResourceUri(),
                verticesData: sideData,
                vertexCount: sideVertexCount, // we have four float32 values per single vertex [x, y, z, aux]
                normalCount: 0,
                texcoordCount: 0,
                indexCount: sideVertexCount,
                faceSize: sideVertexCount / sideAttribCount,
                offset: 0,
                binSize: sideData.byteLength, // size of float32
                svElements: sideElements,
                svStrides: sideStrides,
                svOffsets: sideOffsets,
                sideAttribCount: sideAttribCount
            }
        }];
    }

    private _generateCapGeometryObjectDescriptionJSON(aDescriptor: any, aPolygonOffsetFactor: number, aPolygonOffsetUnits: number) {
        // this goes to gl buffer
        let capData = new Float32Array(this.fitSVGeneratorModule.buffer, aDescriptor.get_caps(), aDescriptor.get_capsSize());

        // these are data to draw triangles from the buffer
        let capAttribCount = 3;
        let capVertexCount = aDescriptor.get_capsSize() / 4; // 4 == attribute size
        let capElements = [];
        let capStrides = [];
        let capOffsets = [];
        for (let i = 0; i < capAttribCount; ++i) {
            let capAttribs = aDescriptor.get_capsVertAttrib(i);
            capElements.push(capAttribs.get_elements());
            capStrides.push(capAttribs.get_stride());
            capOffsets.push(capAttribs.get_offset());
        }

        return [{
            description: {
                type: "SVCAPSHAPE",
                bboxMin: [-1, -1, -1],
                bboxMax: [1, 1, 1]
            },
            appearance: {
                renderOption: {
                    polygonOffsetFactor: aPolygonOffsetFactor, // the 2.0 is used here to get rid of flickering the shadowed geometry
                    polygonOffsetUnits: aPolygonOffsetUnits, // the 2.0 is used here to get rid of flickering the shadowed geometry
                    isCap: true,
                    pickable: false
                }
            },
            svBinaryGeometry: {
                src: this._sceneAPI.getDocument().createLocalResourceUri(),
                verticesData: capData,
                vertexCount: capVertexCount, // we have four float32 values per single vertex [x, y, z, aux]
                normalCount: 0,
                texcoordCount: 0,
                indexCount: capVertexCount,
                faceSize: capVertexCount / capAttribCount,
                offset: 0,
                binSize: capData.byteLength,
                svElements: capElements,
                svStrides: capStrides,
                svOffsets: capOffsets,
                capAttribCount: capAttribCount
            }
        }];
    }
}
